import {forwardRef} from 'react'
import type {ReactNode, SVGProps} from 'react'

type IconWeight = 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone'

type PhosphorIconProps = Omit<SVGProps<SVGSVGElement>, 'height' | 'width'> & {
  size?: number | string
  weight?: IconWeight
}

type IconBody = (weight: IconWeight) => ReactNode

const strokeWidths: Record<IconWeight, number> = {
  thin: 8,
  light: 12,
  regular: 16,
  bold: 24,
  fill: 16,
  duotone: 16,
}

const createIcon = (displayName: string, body: IconBody) => {
  const Icon = forwardRef<SVGSVGElement, PhosphorIconProps>(
    ({size = 24, weight = 'regular', ...props}, ref) => (
      <svg
        {...props}
        ref={ref}
        xmlns='http://www.w3.org/2000/svg'
        width={size}
        height={size}
        viewBox='0 0 256 256'
        fill='none'
        stroke='currentColor'
        strokeWidth={strokeWidths[weight]}
        strokeLinecap='round'
        strokeLinejoin='round'
        focusable='false'
      >
        {body(weight)}
      </svg>
    )
  )

  Icon.displayName = displayName
  return Icon
}

const ArrowRight = createIcon('ArrowRight', () => (
  <>
    <path d='M40 128h176' />
    <path d='m144 56 72 72-72 72' />
  </>
))

const ArrowLeft = createIcon('ArrowLeft', () => (
  <>
    <path d='M216 128H40' />
    <path d='m112 56-72 72 72 72' />
  </>
))

const ArrowsLeftRight = createIcon('ArrowsLeftRight', (weight) =>
  weight === 'fill' ? (
    <path
      d='M168 32v32H40a16 16 0 0 0 0 32h128v32l56-48-56-48ZM88 128l-56 48 56 48v-32h128a16 16 0 0 0 0-32H88v-32Z'
      fill='currentColor'
      stroke='none'
    />
  ) : (
    <>
      <path d='M40 80h176' />
      <path d='m168 32 48 48-48 48' />
      <path d='M216 176H40' />
      <path d='m88 128-48 48 48 48' />
    </>
  )
)

const ChartLineUp = createIcon('ChartLineUp', (weight) =>
  weight === 'fill' ? (
    <>
      <path d='M24 32h24v168h184v24H24Z' fill='currentColor' stroke='none' />
      <path
        d='m62 166 53-54 36 30 53-54v27h24V48h-67v24h27l-39 39-36-30-68 68Z'
        fill='currentColor'
        stroke='none'
      />
    </>
  ) : (
    <>
      {weight === 'duotone' && (
        <path d='m48 184 64-64 40 32 64-72v104Z' fill='currentColor' opacity='0.18' stroke='none' />
      )}
      <path d='M32 32v176a16 16 0 0 0 16 16h176' />
      <path d='m56 168 56-56 40 32 64-72' />
      <path d='M168 72h48v48' />
    </>
  )
)

const List = createIcon('List', () => (
  <>
    <path d='M40 72h176' />
    <path d='M40 128h176' />
    <path d='M40 184h176' />
  </>
))

const Moon = createIcon('Moon', (weight) => (
  <>
    {weight === 'duotone' && (
      <path
        d='M216 152a88 88 0 0 1-112-112 88 88 0 1 0 112 112Z'
        fill='currentColor'
        opacity='0.18'
        stroke='none'
      />
    )}
    <path d='M216 152a88 88 0 0 1-112-112 88 88 0 1 0 112 112Z' />
  </>
))

const PlugsConnected = createIcon('PlugsConnected', (weight) =>
  weight === 'fill' ? (
    <>
      <path
        d='m51 31 38 38 17-17 17 17-17 17 35 35-20 20-35-35-17 17-17-17 17-17-38-38Z'
        fill='currentColor'
        stroke='none'
      />
      <path
        d='m205 225-38-38-17 17-17-17 17-17-35-35 20-20 35 35 17-17 17 17-17 17 38 38Z'
        fill='currentColor'
        stroke='none'
      />
    </>
  ) : (
    <>
      <path d='m48 40 72 72-8 8a34 34 0 0 1-48 0L40 96a34 34 0 0 1 0-48Z' />
      <path d='m208 216-72-72 8-8a34 34 0 0 1 48 0l24 24a34 34 0 0 1 0 48Z' />
      <path d='m72 40-24 24' />
      <path d='m96 64-24 24' />
      <path d='m184 216 24-24' />
      <path d='m160 192 24-24' />
      <path d='m112 120 32 32' />
    </>
  )
)

const SignOut = createIcon('SignOut', () => (
  <>
    <path d='M112 40H48a8 8 0 0 0-8 8v160a8 8 0 0 0 8 8h64' />
    <path d='M144 80h40l40 48-40 48h-40' />
    <path d='M88 128h136' />
  </>
))

const SquaresFour = createIcon('SquaresFour', (weight) =>
  weight === 'fill' ? (
    <path
      d='M32 32h80v80H32Zm112 0h80v80h-80ZM32 144h80v80H32Zm112 0h80v80h-80Z'
      fill='currentColor'
      stroke='none'
    />
  ) : (
    <>
      <rect x='32' y='32' width='80' height='80' rx='12' />
      <rect x='144' y='32' width='80' height='80' rx='12' />
      <rect x='32' y='144' width='80' height='80' rx='12' />
      <rect x='144' y='144' width='80' height='80' rx='12' />
    </>
  )
)

const Sun = createIcon('Sun', (weight) => (
  <>
    {weight === 'duotone' && (
      <circle cx='128' cy='128' r='48' fill='currentColor' opacity='0.18' stroke='none' />
    )}
    <circle cx='128' cy='128' r='40' />
    <path d='M128 24v24' />
    <path d='M128 208v24' />
    <path d='m54.5 54.5 17 17' />
    <path d='m184.5 184.5 17 17' />
    <path d='M24 128h24' />
    <path d='M208 128h24' />
    <path d='m54.5 201.5 17-17' />
    <path d='m184.5 71.5 17-17' />
  </>
))

const Target = createIcon('Target', (weight) =>
  weight === 'fill' ? (
    <>
      <path
        d='M128 24a104 104 0 1 0 104 104A104.1 104.1 0 0 0 128 24Zm0 176a72 72 0 1 1 72-72 72.1 72.1 0 0 1-72 72Z'
        fill='currentColor'
        fillRule='evenodd'
        stroke='none'
      />
      <circle cx='128' cy='128' r='40' fill='currentColor' stroke='none' />
    </>
  ) : (
    <>
      <circle cx='128' cy='128' r='96' />
      <circle cx='128' cy='128' r='56' />
      <circle cx='128' cy='128' r='16' />
    </>
  )
)

const Wallet = createIcon('Wallet', (weight) =>
  weight === 'fill' ? (
    <>
      <rect x='24' y='48' width='208' height='160' rx='24' fill='currentColor' stroke='none' />
      <path
        d='M160 96h72v64h-72a32 32 0 0 1 0-64Z'
        fill='currentColor'
        opacity='0.45'
        stroke='none'
      />
      <circle cx='176' cy='128' r='8' fill='currentColor' stroke='none' />
    </>
  ) : (
    <>
      <rect x='24' y='48' width='208' height='160' rx='24' />
      <path d='M160 96h72v64h-72a32 32 0 0 1 0-64Z' />
      <circle cx='176' cy='128' r='4' fill='currentColor' stroke='none' />
      <path d='M48 48 176 24v24' />
    </>
  )
)

const X = createIcon('X', () => (
  <>
    <path d='M48 48l160 160' />
    <path d='M208 48 48 208' />
  </>
))

const Coins = createIcon('Coins', (weight) => (
  <>
    {weight === 'duotone' && (
      <>
        <ellipse
          cx='104'
          cy='72'
          rx='64'
          ry='32'
          fill='currentColor'
          opacity='0.18'
          stroke='none'
        />
        <ellipse
          cx='152'
          cy='160'
          rx='64'
          ry='32'
          fill='currentColor'
          opacity='0.18'
          stroke='none'
        />
      </>
    )}
    <ellipse cx='104' cy='72' rx='64' ry='32' />
    <path d='M40 72v48c0 17.7 28.7 32 64 32 11.9 0 23.1-1.6 32.6-4.5' />
    <path d='M40 120v40c0 17.7 28.7 32 64 32' />
    <ellipse cx='152' cy='160' rx='64' ry='32' />
    <path d='M88 160v40c0 17.7 28.7 32 64 32s64-14.3 64-32v-40' />
  </>
))

const TrendUp = createIcon('TrendUp', (weight) => (
  <>
    {weight === 'duotone' && (
      <path d='m40 184 64-64 40 40 72-80v104Z' fill='currentColor' opacity='0.18' stroke='none' />
    )}
    <path d='m40 184 64-64 40 40 72-80' />
    <path d='M160 80h56v56' />
  </>
))

const ChartDonut = createIcon('ChartDonut', (weight) => (
  <>
    {weight === 'duotone' && (
      <path d='M128 32a96 96 0 1 0 96 96h-96Z' fill='currentColor' opacity='0.18' stroke='none' />
    )}
    <path d='M128 32a96 96 0 1 0 96 96h-96Z' />
    <path d='M160 32.5A96 96 0 0 1 223.5 96H160Z' />
    <circle cx='128' cy='128' r='40' />
  </>
))

const Flask = createIcon('Flask', (weight) => (
  <>
    {weight === 'duotone' && (
      <path
        d='m83 160-27 46a12 12 0 0 0 10 18h124a12 12 0 0 0 10-18l-27-46Z'
        fill='currentColor'
        opacity='0.18'
        stroke='none'
      />
    )}
    <path d='M96 32h64' />
    <path d='M112 32v64L56 192a16 16 0 0 0 14 24h116a16 16 0 0 0 14-24l-56-96V32' />
    <path d='M80 160h96' />
  </>
))

const Stack = createIcon('Stack', (weight) => (
  <>
    {weight === 'duotone' && (
      <path d='m32 80 96-48 96 48-96 48Z' fill='currentColor' opacity='0.18' stroke='none' />
    )}
    <path d='m32 80 96-48 96 48-96 48Z' />
    <path d='m32 128 96 48 96-48' />
    <path d='m32 176 96 48 96-48' />
  </>
))

const ArrowClockwise = createIcon('ArrowClockwise', () => (
  <>
    <path d='M216 128a88 88 0 1 1-26-62' />
    <path d='M190 34v32h-32' />
  </>
))

const WarningCircle = createIcon('WarningCircle', (weight) => (
  <>
    {weight === 'duotone' && (
      <circle cx='128' cy='128' r='96' fill='currentColor' opacity='0.18' stroke='none' />
    )}
    <circle cx='128' cy='128' r='96' />
    <path d='M128 72v64' />
    <circle cx='128' cy='176' r='8' fill='currentColor' stroke='none' />
  </>
))

const Compass = createIcon('Compass', (weight) => (
  <>
    {weight === 'duotone' && (
      <circle cx='128' cy='128' r='96' fill='currentColor' opacity='0.18' stroke='none' />
    )}
    <circle cx='128' cy='128' r='96' />
    <path d='m160 96-16 48-48 16 16-48Z' />
    <circle cx='128' cy='128' r='8' fill='currentColor' stroke='none' />
  </>
))

export {
  ArrowClockwise,
  ArrowLeft,
  ArrowRight,
  ArrowsLeftRight,
  ChartDonut,
  ChartLineUp,
  Coins,
  Compass,
  Flask,
  List,
  Moon,
  PlugsConnected,
  SignOut,
  SquaresFour,
  Stack,
  Sun,
  Target,
  TrendUp,
  Wallet,
  WarningCircle,
  X,
}
export type {IconWeight, PhosphorIconProps}
